// ** React Imports
import { useState, useEffect, useCallback } from 'react';

// ** Next Import
import { Link } from 'react-router-dom';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { DataGrid } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';

// ** Custom Components Imports
import TableHeader from 'src/features/accounts/TableHeader';
import EditAccountDrawer from 'src/features/accounts/EditAccountDrawer';
import { client } from 'src/utils/axios-client';
import { getAccountsEndpoint } from 'src/configs/endpoints';
import { AccountStore, Account, AccountKeyword } from 'src/stores/AccountStore';

import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import PencilOutline from 'mdi-material-ui/PencilOutline';
import RoleSelector from '../roles/RoleSelector';
import RenderIf, { ClaimType, ClaimValue } from '../common/RenderIf';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../hooks/useAwaitableComponent';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { ApiResponseType } from '../../types';
import ApplicationStore from '../../stores/ApplicationStore';
import PageHeader from '../../materio/@core/components/page-header';
import useAccountImport from '../../v2/features/Accounts/AccountImport/hooks/AccountImport.hooks';
import AccountImport from '../../v2/features/Accounts/AccountImport/AccountImport';

interface CellType {
    row: Account;
}

const AccountList = () => {
    const { openAccountImport } = useAccountImport();
    const [pageSize, setPageSize] = useState(10);
    const [value, setValue] = useState<string>('');
    const [editOpen, setEditOpen] = useState<boolean>(false);
    const list = AccountStore.useState((s) => s.accounts);
    const [filteredList, setFilteredList] = useState([] as Account[]);
    const [selectedAccount, setSelectedAccount] = useState(undefined as undefined | Account);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== 'idle';

    useEffect(() => {
        loadRecords(0, pageSize);
    }, []);

    useEffect(() => {
        setFilteredList(
            list.filter(
                (u) =>
                    u.id !== '00000000-0000-0000-0000-000000000000' &&
                    (value == '' || u.name.toUpperCase().includes(value.toUpperCase())),
            ),
        );
    }, [list, value]);

    const handleConfirmDelete = async (row: Account) => {
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/accounts', { params: { accountId: row.id } });

            if (response.data.succeeded) {
                await loadRecords(0, pageSize);

                ApplicationStore.update((s) => {
                    s.snackbarMessage = 'Account successfully deleted';
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting account.');
            }
        } catch (err) {
        } finally {
            reset();
        }
    };

    const columns = [
        {
            flex: 0.5,
            field: 'name',
            headerName: 'Name',
            renderCell: ({ row }: CellType) => {
                const { id, name } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'hqCountryName',
            headerName: 'Country',
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.hqCountryName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'industryName',
            headerName: 'Industry',
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.industryName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'revenueName',
            headerName: 'Revenue',
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.revenueName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'employeeSizeName',
            headerName: 'Employee Size',
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.employeeSizeName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: '',
            maxWidth: 150,
            headerName: 'Actions',
            renderCell: ({ row }: CellType) => {
                return (
                    <>
                        <RenderIf claimType={ClaimType.Accounts} claimValue={ClaimValue.Delete}>
                            <IconButton onClick={() => handleConfirmDelete(row)} title="Delete">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </RenderIf>
                        <RenderIf claimType={ClaimType.Accounts} claimValue={ClaimValue.Edit}>
                            <IconButton onClick={() => onRowClick(row)} title="Edit">
                                <PencilOutline fontSize="small" />
                            </IconButton>
                        </RenderIf>

                        <RoleSelector claimType={ClaimType.Accounts} recordId={row.id} />
                    </>
                );
            },
        },
    ];

    const loadRecords = async (pageNumber: number, pageSize: number) => {
        const response = await client.get<Account[]>(getAccountsEndpoint, { params: { pageNumber, pageSize } });

        AccountStore.update((s) => {
            s.accounts = response.data;
        });
    };

    const handleFilter = useCallback((val: string) => {
        setValue(val);
    }, []);

    const toggleEditAccountDrawer = async () => {
        if (editOpen) {
            await loadRecords(1, pageSize);
        }
        setEditOpen(!editOpen);
    };

    const onRowClick = (row: Account) => {
        setSelectedAccount(row);
        setEditOpen(true);
    };

    const onNewAccount = () => {
        setSelectedAccount({
            id: uuidv4(),
            name: '',
            industryId: '',
            hqAddress: '',
            hqProvince: '',
            revenueId: '',
            employeeSizeId: '',
            accountStatusId: '',
            hqCountryId: '',
            hqPostalCode: '',
            hqRegionId: '',
            keywords: [] as AccountKeyword[],
        } as Account);

        setEditOpen(true);
    };

    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <PageHeader
                    title={<Typography variant="h5">Account List</Typography>}
                    subtitle={<Typography variant="body2">Create and manage accounts.</Typography>}
                />
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <TableHeader
                        value={value}
                        handleFilter={handleFilter}
                        onAddAccount={onNewAccount}
                        onImportAccounts={openAccountImport}
                    />
                    <DataGrid
                        autoHeight
                        pagination
                        rows={filteredList}
                        columns={columns}
                        pageSize={pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
                    />
                </Card>
            </Grid>

            {selectedAccount && (
                <EditAccountDrawer
                    open={editOpen}
                    onSaved={toggleEditAccountDrawer}
                    onClose={toggleEditAccountDrawer}
                    account={selectedAccount}
                    key={selectedAccount.id}
                />
            )}
            <ConfirmationDialog
                open={showModal}
                onConfirm={resolve}
                onCancel={reject}
                title="Delete?"
                message="Are you sure you want to delete the account?"
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
            />

            <AccountImport
                onContinue={(accounts: Account[]) => {
                    loadRecords(0, pageSize);
                }}
            />
        </Grid>
    );
};

export { AccountList };
export default AccountList;
